import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiLock } from "react-icons/fi";
import { searchTagsClient, type SearchTag } from "./searchTagsClient";

/**
 * Read-only view of the Search Tags — the curated filter primitives that power the public `/search`
 * sidebar. Tags are reference data defined in code (C# manifest) and seeded by the
 * ReferenceDataReconciler; the controller exposes GET only, so there is no create/edit/delete here.
 *
 * Each tag has (1) trigger keywords and (2) default content type ids; the deploy-time indexer attaches
 * a tag to an entry when its title/summary/body contains a keyword OR its content type is in the list.
 */
const SearchTagsPage: React.FC = () => {
	const [tags, setTags] = useState<SearchTag[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const data = await searchTagsClient.list(true);
			setTags(data);
		} catch (e: any) {
			setError(e?.message || "Échec du chargement des tags");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const sortedTags = useMemo(
		() => [...tags].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, "fr")),
		[tags],
	);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-8">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Search Tags</p>
						<h1 className="text-2xl font-semibold text-slate-900">Tags de recherche</h1>
						<p className="text-xs text-slate-500">
							Groupements éditoriaux affichés dans la barre latérale du moteur de recherche public.
						</p>
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-3">
						<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
							<FiLock className="h-3 w-3" /> Géré dans le code
						</span>
						<div className="text-sm text-slate-500">{tags.length} tag{tags.length === 1 ? "" : "s"}</div>
						<button
							type="button"
							onClick={refresh}
							disabled={loading}
							className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{loading ? "Chargement…" : "Rafraîchir"}
						</button>
					</div>
				</div>

				{error && (
					<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
				)}

				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
									<th className="px-5 py-3">Ordre</th>
									<th className="px-5 py-3">Tag</th>
									<th className="px-5 py-3">Mots-clés déclencheurs</th>
									<th className="px-5 py-3">Types par défaut</th>
									<th className="px-5 py-3">Actif</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading ? (
									<tr>
										<td colSpan={5} className="px-5 py-6 text-center text-slate-500">
											Chargement des tags…
										</td>
									</tr>
								) : sortedTags.length === 0 ? (
									<tr>
										<td colSpan={5} className="px-5 py-10 text-center text-slate-500">
											Aucun tag défini.
										</td>
									</tr>
								) : (
									sortedTags.map((tag) => (
										<tr key={tag.id} className="hover:bg-slate-50">
											<td className="px-5 py-3 font-mono text-xs text-slate-500">{tag.sortOrder}</td>
											<td className="px-5 py-3">
												<div className="flex items-center gap-2">
													{tag.color ? (
														<span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: tag.color }} />
													) : null}
													<div>
														<div className="font-medium text-slate-900">{tag.label}</div>
														<div className="font-mono text-[11px] text-slate-400">{tag.id}</div>
													</div>
												</div>
												{tag.description ? <div className="mt-1 text-xs text-slate-500">{tag.description}</div> : null}
											</td>
											<td className="px-5 py-3">
												<div className="flex max-w-md flex-wrap gap-1">
													{tag.triggerKeywords.length === 0 ? (
														<span className="text-xs italic text-slate-400">aucun</span>
													) : (
														tag.triggerKeywords.slice(0, 6).map((kw) => (
															<span key={kw} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
																{kw}
															</span>
														))
													)}
													{tag.triggerKeywords.length > 6 ? (
														<span className="text-[11px] text-slate-500">+{tag.triggerKeywords.length - 6}</span>
													) : null}
												</div>
											</td>
											<td className="px-5 py-3">
												<div className="flex max-w-md flex-wrap gap-1">
													{tag.defaultContentTypeIds.length === 0 ? (
														<span className="text-xs italic text-slate-400">aucun</span>
													) : (
														tag.defaultContentTypeIds.slice(0, 4).map((id) => (
															<span key={id} className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-mono text-blue-700">
																{id}
															</span>
														))
													)}
													{tag.defaultContentTypeIds.length > 4 ? (
														<span className="text-[11px] text-slate-500">+{tag.defaultContentTypeIds.length - 4}</span>
													) : null}
												</div>
											</td>
											<td className="px-5 py-3">
												<span
													className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
														tag.isActive ? "bg-emerald-50 text-emerald-700" : "bg-slate-200 text-slate-600"
													}`}
												>
													{tag.isActive ? "Actif" : "Inactif"}
												</span>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</section>

				<div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">
					<p>
						<strong>Comment ça marche :</strong> chaque tag a (1) une liste de mots-clés déclencheurs et (2) une liste
						d'IDs de types de contenu par défaut. Lors de la reconstruction de l'index, chaque entrée reçoit un tag si
						son titre/résumé/corps contient un mot-clé OU si son type fait partie des types par défaut. Les tags sont
						définis dans le code (manifeste C#) ; modifiez-les là puis relancez la reconstruction de l'index.
					</p>
				</div>
			</div>
		</div>
	);
};

export default SearchTagsPage;
